import React, { useState } from 'react';
import './DocsPage.css';

const sections = [
  {
    id: 'getting-started',
    icon: '🚀',
    title: 'Getting Started',
    articles: [
      { title: 'Installation & Setup', desc: 'Install UltraCoder IDE on macOS, Windows, and Linux in under 2 minutes.' },
      { title: 'Quick Start Guide', desc: 'Open your first project, activate the AI assistant, and write your first AI-generated function.' },
      { title: 'System Requirements', desc: 'Minimum and recommended hardware specs for the best experience.' },
    ],
  },
  {
    id: 'ai-features',
    icon: '🤖',
    title: 'AI Features',
    articles: [
      { title: 'AI Code Completion', desc: 'Context-aware multi-line completions powered by our fine-tuned model.' },
      { title: 'AI Code Generation', desc: 'Describe what you want in plain English and get production-ready code instantly.' },
      { title: 'AI Code Review', desc: 'Automated bug detection, security scanning, and refactoring suggestions.' },
      { title: 'Chat with your Codebase', desc: 'Ask questions about any file, function, or module using natural language.' },
    ],
  },
  {
    id: 'collaboration',
    icon: '🤝',
    title: 'Collaboration',
    articles: [
      { title: 'Real-Time Editing', desc: 'Invite teammates and see live cursors, edits, and AI suggestions from everyone.' },
      { title: 'Shared Sessions', desc: 'Start a session and share a link — no account required for guests.' },
      { title: 'Comments & Reviews', desc: 'Leave inline code comments and request reviews without leaving the editor.' },
    ],
  },
  {
    id: 'integrations',
    icon: '🔌',
    title: 'Integrations',
    articles: [
      { title: 'Git & GitHub', desc: 'Built-in Git support with AI-generated commit messages and PR descriptions.' },
      { title: 'VS Code Extension', desc: 'Use UltraCoder AI inside VS Code via our official extension.' },
      { title: 'REST API', desc: 'Integrate UltraCoder AI capabilities into your own tools and pipelines.' },
      { title: 'CI/CD Pipelines', desc: 'Run AI code review and lint checks inside GitHub Actions, GitLab CI, or Jenkins.' },
    ],
  },
  {
    id: 'configuration',
    icon: '⚙️',
    title: 'Configuration',
    articles: [
      { title: 'Editor Settings', desc: 'Customize keybindings, themes, fonts, and editor behavior.' },
      { title: 'AI Model Selection', desc: 'Choose between speed-optimised and quality-optimised AI models per project.' },
      { title: 'Environment Variables', desc: 'Manage project-level and workspace-level env vars securely inside the IDE.' },
    ],
  },
  {
    id: 'security',
    icon: '🔒',
    title: 'Security & Privacy',
    articles: [
      { title: 'Data Privacy Policy', desc: 'Your code is never stored on our servers unless you explicitly opt in to training.' },
      { title: 'SOC 2 Compliance', desc: 'UltraCoder IDE is SOC 2 Type II certified. Audit reports available on request.' },
      { title: 'On-Premise Deployment', desc: 'Run the entire stack on your own infrastructure for maximum control.' },
    ],
  },
];

const DocsPage = () => {
  const [search, setSearch] = useState('');
  const [activeSection, setActiveSection] = useState('getting-started');
  const docsUrl = process.env.REACT_APP_DOCS_URL || '#';
  const githubUrl = process.env.REACT_APP_GITHUB_URL || '#';
  const discordUrl = process.env.REACT_APP_DISCORD_URL || '#';
  const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || 'support@ultracoder.dev';

  const filtered = sections.map((s) => ({
    ...s,
    articles: s.articles.filter(
      (a) =>
        search === '' ||
        a.title.toLowerCase().includes(search.toLowerCase()) ||
        a.desc.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter((s) => s.articles.length > 0);

  const current = sections.find((s) => s.id === activeSection) || sections[0];

  return (
    <div className="docs-page">
      {/* Hero */}
      <section className="docs-page__hero">
        <div className="docs-page__hero-bg" />
        <div className="docs-page__container">
          <div className="docs-page__badge">
            <span>📚</span> Documentation
          </div>
          <h1 className="docs-page__title">
            Everything you need to <br />
            <span className="docs-page__title-gradient">master UltraCoder</span>
          </h1>
          <div className="docs-page__search-wrap">
            <span className="docs-page__search-icon">🔍</span>
            <input
              className="docs-page__search"
              type="text"
              placeholder="Search documentation…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Body */}
      <div className="docs-page__body">
        <div className="docs-page__container docs-page__layout">
          {/* Sidebar */}
          <aside className="docs-page__sidebar">
            <nav>
              {sections.map((s) => (
                <button
                  key={s.id}
                  className={`docs-page__nav-item ${activeSection === s.id ? 'docs-page__nav-item--active' : ''}`}
                  onClick={() => { setActiveSection(s.id); setSearch(''); }}
                >
                  <span>{s.icon}</span>
                  {s.title}
                </button>
              ))}
            </nav>

            <div className="docs-page__sidebar-links">
              <a href={docsUrl} target="_blank" rel="noreferrer">📖 Full Docs Site</a>
              <a href={githubUrl} target="_blank" rel="noreferrer">🐙 GitHub</a>
              <a href={discordUrl} target="_blank" rel="noreferrer">💬 Discord</a>
              <a href={`mailto:${supportEmail}`}>✉️ Email Support</a>
            </div>
          </aside>

          {/* Main content */}
          <main className="docs-page__main">
            {search ? (
              <>
                <h2 className="docs-page__section-title">
                  Search results for &ldquo;{search}&rdquo;
                </h2>
                {filtered.length === 0 ? (
                  <div className="docs-page__empty">
                    <span>🔍</span>
                    <p>No results found. Try a different keyword.</p>
                  </div>
                ) : (
                  filtered.map((s) => (
                    <div key={s.id} className="docs-page__group">
                      <h3 className="docs-page__group-title">{s.icon} {s.title}</h3>
                      <div className="docs-page__articles">
                        {s.articles.map((a) => (
                          <a key={a.title} href={docsUrl} target="_blank" rel="noreferrer" className="docs-card">
                            <span className="docs-card__title">{a.title}</span>
                            <p className="docs-card__desc">{a.desc}</p>
                            <span className="docs-card__arrow">→</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </>
            ) : (
              <>
                <h2 className="docs-page__section-title">
                  {current.icon} {current.title}
                </h2>
                <div className="docs-page__articles">
                  {current.articles.map((a) => (
                    <a key={a.title} href={docsUrl} target="_blank" rel="noreferrer" className="docs-card">
                      <span className="docs-card__title">{a.title}</span>
                      <p className="docs-card__desc">{a.desc}</p>
                      <span className="docs-card__arrow">→</span>
                    </a>
                  ))}
                </div>
              </>
            )}

            {/* Help strip */}
            <div className="docs-page__help-strip">
              <div className="docs-page__help-icon">💡</div>
              <div>
                <h4>Can't find what you're looking for?</h4>
                <p>
                  Ask in our <a href={discordUrl} target="_blank" rel="noreferrer">Discord community</a> or
                  email us at <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
                </p>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default DocsPage;